import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { CallbackPicker, useCallbackTime } from "@/features/callback-picker";
import { ApiError } from "@/lib/api";
import { callbackWhen } from "@/lib/callbacks";
import { useAuth } from "@/lib/auth";
import { contactName, exitProtected, outcomeEffect, outcomeSaved, stageText } from "@/lib/contacts";
import { useCallDispositions, useCampaigns, useContactOutcome } from "@/lib/queries";
import { atLeast, type Disposition, type LeadCard } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Set outcome — the off-call disposition drawer (ADR-044).
 *
 * The same buttons a rep sees after a call, with a one-line effect under each and the next-action
 * sentence printed before Save, so nobody guesses what the click does. Core-api decides all of it
 * (`POST /leads/{id}/disposition`: the stage, the note, a do-not-call, a hold on every list row, a
 * callback at the time picked on the lead's clock — ADR-080, the handover to the campaign the
 * outcome names); the words here say it first. A working outcome on a lead that has left is dimmed
 * with the reason: the exit is protected (ADR-046), and core-api would refuse it anyway.
 */
export function ContactOutcomeDialog({
  open, onOpenChange, contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const outcomes = useCallDispositions(contact.calling_campaign?.id ?? null, open);
  // The names of the campaigns outcomes hand a lead to. A rep cannot list campaigns, and reads
  // "its campaign" instead.
  const campaigns = useCampaigns({}, { enabled: open && atLeast(user?.role, "supervisor") });
  const handsTo = (outcome: Disposition) =>
    campaigns.data?.find((c) => c.id === outcome.enlist_campaign_id)?.name ?? null;
  const record = useContactOutcome();
  const callback = useCallbackTime(contact.clock_timezone ?? contact.timezone);
  const [chosen, setChosen] = React.useState<Disposition | null>(null);
  const [note, setNote] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setChosen(null);
    setNote("");
    setError(null);
  }, [open]);

  const offered = outcomes.data ?? [];
  const needsNote = chosen?.requires_note ?? false;
  const timed = chosen?.kind === "callback";
  const canSave =
    chosen !== null && (!needsNote || note.trim().length > 0) && (!timed || callback.value.iso !== null);
  const lead = callback.value.leadClock ? callback.value.zone : null;

  const save = () => {
    if (!chosen) return;
    setError(null);
    record.mutate(
      {
        leadId: contact.id,
        disposition: chosen.slug,
        note: note.trim() || null,
        callbackAt: timed ? callback.value.iso : null,
      },
      {
        onSuccess: (result) => {
          const when = result.callback_at ? callbackWhen(result.callback_at, lead).there : null;
          toast.success(`${chosen.label} recorded`, outcomeSaved(result, when));
          onOpenChange(false);
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "Could not record the outcome."),
      },
    );
  };

  const target = chosen?.category === "compliance" ? "dnc" : chosen?.stage ?? null;
  const moves = chosen !== null && target !== null && target !== contact.stage;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="md">
        <DialogHeader>
          <DialogTitle>Set outcome</DialogTitle>
          <DialogDescription>
            {contactName(contact) ?? contact.phone} · recorded by you, now, off a call. It moves the stage
            and keeps the note, and a callback is dialed at the time you pick.
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save();
          }}
        >
          <DialogBody className="space-y-4">
            <DiscardGuard changed={chosen !== null || note.trim().length > 0} />
            {outcomes.isPending ? (
              <div className="flex justify-center py-8">
                <Spinner className="size-4" />
              </div>
            ) : outcomes.error ? (
              <Callout tone="danger">
                {outcomes.error instanceof ApiError ? outcomes.error.message : "Could not load the outcomes."}
              </Callout>
            ) : offered.length === 0 ? (
              <p className="text-sm text-text-muted">
                The calling campaign offers no outcomes. An admin picks them on its Dispositions tab.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2" role="group" aria-label="Outcomes">
                {offered.map((outcome) => {
                  const off = exitProtected(contact, outcome);
                  const lit = chosen?.id === outcome.id;
                  return (
                    <button
                      key={outcome.id}
                      type="button"
                      disabled={off !== null || record.isPending}
                      aria-pressed={lit}
                      title={off ?? undefined}
                      onClick={() => {
                        setChosen(outcome);
                        if (outcome.kind === "callback" && chosen?.id !== outcome.id) callback.start(outcome);
                      }}
                      className={cn(
                        "rounded-lg border border-border bg-surface px-3 py-2 text-left transition-colors",
                        "hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50",
                        lit && "border-accent-8 bg-accent-2",
                      )}
                    >
                      <span className={cn("block text-sm font-medium", lit ? "text-accent-11" : "text-text")}>
                        {outcome.label}
                      </span>
                      <span className="block text-2xs text-text-muted">
                        {off ?? outcomeEffect(outcome, handsTo(outcome))}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {chosen && timed && (
              <CallbackPicker value={callback.value} onChange={callback.set} disabled={record.isPending} />
            )}

            {chosen && (
              <Field
                label={needsNote ? `Note · required for ${chosen.label}` : "Note · kept on the record"}
                htmlFor="outcome-note"
                required={needsNote}
              >
                <Textarea
                  rows={3}
                  maxLength={4000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="font-sans text-sm"
                  placeholder="What was said, and what happens next"
                />
              </Field>
            )}

            {chosen && (
              <p className="text-xs text-text-muted" aria-live="polite">
                <span className="font-medium text-text">Next:</span>{" "}
                {timed && callback.value.instant !== null
                  ? `called back ${callbackWhen(callback.value.instant, lead).there}, first by the rep who last spoke to them`
                  : outcomeEffect(chosen, handsTo(chosen))}
                .{" "}
                {moves ? (
                  <>
                    Stage {stageText(contact.stage).label} → {stageText(target).label}, since now.
                  </>
                ) : (
                  <>The stage stays {stageText(contact.stage).label}.</>
                )}
              </p>
            )}

            {error && <Callout tone="danger">{error}</Callout>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={record.isPending} disabled={!canSave}>
              Save outcome
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
